// map-engine.jsx — procedural city-map generator + poster renderer
// Exports to window: MAP, generateCity, THEMES, MapPoster
(function () {
  const MAP_SIZE = 2400;

  // --- seeded RNG ---
  function hashString(s) {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // wavy polyline between two points with perpendicular jitter
  function wavy(x1, y1, x2, y2, rng, amp, segs) {
    segs = segs || 3;
    const dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len, ny = dx / len;
    const pts = [];
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      const off = (i === 0 || i === segs) ? 0 : (rng() - 0.5) * amp;
      pts.push([x1 + dx * t + nx * off, y1 + dy * t + ny * off]);
    }
    return pts;
  }
  const toPts = (a) => a.map((p) => p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");

  function generateCity(seedStr) {
    const rng = mulberry32(hashString(seedStr || "anywhere"));
    const S = MAP_SIZE, pad = 500;
    const roads = { minor: [], major: [], highway: [] };
    const parks = [];
    const rivers = [];
    const lakes = [];

    const minorGap = 78 + rng() * 46;          // block size
    const majorEvery = 3 + Math.floor(rng() * 2);
    const jit = minorGap * 0.22;
    const rot = (rng() - 0.5) * 0.42;           // global rotation (radians)

    // vertical streets
    let vi = 0;
    for (let x = -pad; x <= S + pad; x += minorGap, vi++) {
      const drift = (rng() - 0.5) * minorGap * 1.2;
      const pts = wavy(x, -pad, x + drift, S + pad, rng, jit, 4);
      (vi % majorEvery === 0 ? roads.major : roads.minor).push(pts);
    }
    // horizontal streets
    let hi = 0;
    for (let y = -pad; y <= S + pad; y += minorGap, hi++) {
      const drift = (rng() - 0.5) * minorGap * 1.2;
      const pts = wavy(-pad, y, S + pad, y + drift, rng, jit, 4);
      (hi % majorEvery === 0 ? roads.major : roads.minor).push(pts);
    }
    // a few diagonal avenues (highways)
    const nDiag = 2 + Math.floor(rng() * 3);
    for (let i = 0; i < nDiag; i++) {
      const x1 = rng() * S, x2 = rng() * S;
      roads.highway.push(wavy(x1, -pad, x2, S + pad, rng, 120, 5));
    }

    // river: enters top-ish, exits bottom/side
    if (rng() > 0.25) {
      const sx = rng() * S, ex = rng() * S;
      rivers.push(wavy(sx, -pad, ex, S + pad, rng, 360, 6));
    }
    // lake near a random corner
    if (rng() > 0.5) {
      const cx = rng() > 0.5 ? S * (0.12 + rng() * 0.12) : S * (0.76 + rng() * 0.12);
      const cy = rng() > 0.5 ? S * (0.12 + rng() * 0.12) : S * (0.76 + rng() * 0.12);
      lakes.push(blob(cx, cy, 180 + rng() * 160, rng, 10));
    }
    // parks
    const nPark = 3 + Math.floor(rng() * 4);
    for (let i = 0; i < nPark; i++) {
      const cx = S * (0.12 + rng() * 0.76);
      const cy = S * (0.12 + rng() * 0.76);
      parks.push(blob(cx, cy, 90 + rng() * 150, rng, 9));
    }

    // fake coordinates
    const lat = (rng() * 130 - 60);
    const lng = (rng() * 340 - 170);
    const coords =
      Math.abs(lat).toFixed(4) + "° " + (lat >= 0 ? "N" : "S") + "  /  " +
      Math.abs(lng).toFixed(4) + "° " + (lng >= 0 ? "E" : "W");

    return { roads, parks, rivers, lakes, rot, coords, size: S };
  }

  function blob(cx, cy, r, rng, n) {
    const pts = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = r * (0.62 + rng() * 0.5);
      pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
    }
    return pts;
  }

  const THEMES = {
    vintage:  { name: "Lavender",  bg: "#f5f0fa", water: "#b8a8d8", park: "#d8c8e8", minor: "#ffffff", major: "#ffffff", highway: "#9a6bb8", casing: "#f1daff", building: "#ece0f2", buildingStroke: "#cdcac6", labelColor: "#7c5cb0", text: "#7a5a92", marker: "#7c5cb0" },
    sage:     { name: "Coral Reef",     bg: "#fff6e8", water: "#2fb8b0", park: "#8fd4bc", minor: "#f0c8a0", major: "#f0c8a0", highway: "#f2765a", highwayCasing: "#e9ac77", building: "#ffe8c8", rail: "#f0a888", naturalOpacity: 0.2, text: "#a8543a", marker: "#d9593f" },
    blush:    { name: "Honey",          bg: "#ecdfba", water: "#d8bf74", park: "#e4d199", minor: "#ffffff", major: "#ffffff", highway: "#ffffff", text: "#6a541c", marker: "#7a4a6e" },
    sky:      { name: "Harbor Blue",    hidePaths: true, roadW: { highway: 3.6, arterial: 5, minor: 2.8, service: 1, streetLimited: 2.8 }, bg: "#eef5f8", water: "#a8d4e8", park: "#d4e8dc", naturalOpacity: 0, minor: "#ffffff", major: "#ffffff", highway: "#5a8aa8", casing: "#b5dbf2", building: "#dce8ec", buildingStroke: "#cdcac6", buildingStrokeW: 1, buildingStrokeOpacity: 1, rail: "#7aa8bc", mapLabelColor: "#4a6a7a", labelHalo: "#cccccc", waterLabelColor: "#2a6a8a", labelColor: "#4a6a7a", text: "#4a6a7a", marker: "#4a6a7a", defaultMarkerColor: "#4a6a7a" },
    midnight: { name: "Midnight Blue",  bg: "#0f1c2e", water: "#1c3350", park: "#16283a", naturalOpacity: 0.4, minor: "#7f9cbb", major: "#a8c6e0", highway: "#c9a86a", casing: "#16263a", building: "#17263a", buildingStroke: "#243b54", buildingStrokeOpacity: 0.55, rail: "#3f5c7d", mapLabelColor: "#9dbad6", waterLabelColor: "#7d9dbd", labelColor: "#ece0c6", text: "#ece0c6", marker: "#d9b876", defaultMarkerColor: "#d9b876" },
    ink:      { name: "Ink & Bone",     bg: "#e6dfce", water: "#a9c6d6", park: "#c3d3ad", minor: "#ffffff", major: "#ffffff", highway: "#ffffff", text: "#2a2722", labelColor: "#2a2722", marker: "#2a2722", defaultMarkerColor: "#a5342c" },
    violet:   { name: "Sakura",  bg: "#fdf2f2", water: "#cfe0ec", park: "#f3c9d6", minor: "#ffffff", major: "#ffffff", highway: "#e88ba3", casing: "#ebd3d3", building: "#f5e3e0", buildingStroke: "#cdcac6", rail: "#d9a8b8", naturalOpacity: 0.8, text: "#8a5a64", marker: "#d76a86" },
    blueprint: { name: "Greyscale",     bg: "#fffffc", water: "#bcd7e6", park: "#e5e5e5", minor: "#040404", major: "#040404", highway: "#040404", building: "#ededea", buildingStroke: "#666666", rail: "#bbbbbb", naturalOpacity: 1, labelColor: "#2a2722", text: "#9e9e9e", marker: "#c0392b" },
    // Aerial photo styles — satellite:true switches TileMap to Esri World
    // Imagery raster tiles; photoFilter (optional) tones the photo. bg/text
    // style the caption band; mapBg is the map-area loading colour.
    aerial:     { name: "Aerial Photo",   satellite: true, mapBg: "#39414a", bg: "#f4f1ea", water: "#31465a", park: "#3e5a3c", minor: "#9aa1a8", major: "#8d877a", highway: "#3d3933", text: "#2a2722", marker: "#e8502f" },
    aerialNoir: { name: "Black & White",  satellite: true, photoFilter: "grayscale(1) contrast(1.1) brightness(1.02)", mapBg: "#3c3c3c", bg: "#f4f2ee", water: "#6b6b6b", park: "#8a8a8a", minor: "#a8a8a8", major: "#8d877a", highway: "#3d3933", text: "#2a2722", marker: "#b5482e" },
  };

  // ---------- React poster ----------
  function MapPoster(props) {
    const {
      city, theme, view, paper, labels, markerStyle,
      showWater = true, showParks = true, grain = true,
      titleFont, detail = "rich",
    } = props;
    const e = React.createElement;
    const S = MAP_SIZE;

    // poster geometry
    const aspect = paper.aspect;                 // height / width
    const band = labels.show ? (paper.landscape ? 0.2 : 0.18) : 0;        // bottom text band fraction
    const W = 1000, H = W * aspect;
    const mapH = H * (1 - band);
    const mapAspect = W / mapH;                   // w/h of map window

    // view window in map units
    const vw = view.vw;
    const vh = vw / mapAspect;
    const cx = view.cx, cy = view.cy;
    const vb = `${cx - vw / 2} ${cy - vh / 2} ${vw} ${vh}`;

    const rotDeg = (city.rot * 180) / Math.PI;
    const rotXform = `rotate(${rotDeg} ${S / 2} ${S / 2})`;

    // stroke widths (map units)
    const wMinor = detail === "minimal" ? 0 : 3.4;
    const wMajor = 7;
    const wHwy = 12;
    const wRiver = 30;

    const roadEls = [];
    if (showWater) {
      city.lakes.forEach((p, i) => roadEls.push(e("polygon", { key: "lk" + i, points: toPts(p), fill: theme.water })));
      city.rivers.forEach((p, i) => roadEls.push(e("polyline", { key: "rv" + i, points: toPts(p), fill: "none", stroke: theme.water, strokeWidth: wRiver, strokeLinecap: "round", strokeLinejoin: "round" })));
    }
    if (showParks) city.parks.forEach((p, i) => roadEls.push(e("polygon", { key: "pk" + i, points: toPts(p), fill: theme.park })));
    if (wMinor > 0) city.roads.minor.forEach((p, i) => roadEls.push(e("polyline", { key: "mn" + i, points: toPts(p), fill: "none", stroke: theme.minor, strokeWidth: wMinor, strokeLinecap: "round" })));
    city.roads.major.forEach((p, i) => roadEls.push(e("polyline", { key: "mj" + i, points: toPts(p), fill: "none", stroke: theme.major, strokeWidth: wMajor, strokeLinecap: "round" })));
    city.roads.highway.forEach((p, i) => roadEls.push(e("polyline", { key: "hw" + i, points: toPts(p), fill: "none", stroke: theme.highway, strokeWidth: wHwy, strokeLinecap: "round" })));

    // marker (upright, at map centre)
    let markerEl = null;
    const mk = theme.marker;
    if (markerStyle === "pin") {
      markerEl = e("g", { key: "mk", transform: `translate(${S / 2} ${S / 2})` }, [
        e("path", { key: "p", d: "M0,0 C-15,-28 -20,-36 -20,-50 a20,20 0 1,1 40,0 C20,-36 15,-28 0,0 Z", fill: mk }),
        e("circle", { key: "c", cx: 0, cy: -50, r: 7.5, fill: theme.bg }),
      ]);
    } else if (markerStyle === "dot") {
      markerEl = e("g", { key: "mk", transform: `translate(${S / 2} ${S / 2})` }, [
        e("circle", { key: "o", cx: 0, cy: 0, r: 17, fill: "none", stroke: mk, strokeWidth: 6 }),
        e("circle", { key: "i", cx: 0, cy: 0, r: 6, fill: mk }),
      ]);
    } else if (markerStyle === "heart") {
      markerEl = e("g", { key: "mk", transform: `translate(${S / 2} ${S / 2})` },
        e("path", { d: "M0,-6 C-7,-22 -32,-18 -32,2 C-32,22 -10,30 0,44 C10,30 32,22 32,2 C32,-18 7,-22 0,-6 Z", fill: mk }));
    }

    const grainId = "grain-" + (props.id || "0");

    return e("div", {
      className: "poster", style: {
        width: "100%", height: "100%", display: "flex", flexDirection: "column",
        background: theme.bg, color: theme.text, position: "relative", overflow: "hidden", containerType: "size",
      },
    }, [
      // map area
      e("div", { key: "map", className: "poster-map", style: { flex: "1 1 auto", minHeight: 0, position: "relative" } },
        e("svg", {
          width: "100%", height: "100%", viewBox: vb, preserveAspectRatio: "xMidYMid slice",
          style: { display: "block" },
        }, [
          e("rect", { key: "bg", x: -S, y: -S, width: 3 * S, height: 3 * S, fill: theme.bg }),
          e("g", { key: "rot", transform: rotXform }, roadEls),
          markerEl,
        ])
      ),
      // grain overlay
      grain ? e("svg", {
        key: "gr", width: "100%", height: "100%",
        style: { position: "absolute", inset: 0, pointerEvents: "none", mixBlendMode: "multiply", opacity: 0.06 },
      }, [
        e("filter", { key: "f", id: grainId }, e("feTurbulence", { type: "fractalNoise", baseFrequency: "0.9", numOctaves: 2, stitchTiles: "stitch" })),
        e("rect", { key: "r", width: "100%", height: "100%", filter: `url(#${grainId})` }),
      ]) : null,
      // text band
      labels.show ? e("div", {
        key: "txt", className: "poster-text",
        style: {
          flex: "0 0 auto", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", textAlign: "center",
          padding: "5.5% 6% 6.5%",
          gap: "0.3em",
        },
      }, [
        labels.subtitleTop ? e("div", {
          key: "div", style: { display: "flex", alignItems: "center", gap: "0.9em", width: "62%", color: theme.major },
        }, [
          e("span", { key: "l", style: { flex: 1, height: 1, background: "currentColor", opacity: 0.6 } }),
          e("span", { key: "d", style: { width: 5, height: 5, borderRadius: "50%", background: "currentColor" } }),
          e("span", { key: "r", style: { flex: 1, height: 1, background: "currentColor", opacity: 0.6 } }),
        ]) : null,
        e("div", {
          key: "t", style: {
            fontFamily: titleFont, fontWeight: 600, letterSpacing: labels.spaced ? "0.12em" : "0.02em",
            textTransform: labels.upper ? "uppercase" : "none",
            fontSize: "clamp(13px, " + (paper.landscape ? 5 : 5.4) + "cqh, 72px)", lineHeight: 1.04,
          },
        }, labels.title || "Your Place"),
        labels.subtitle ? e("div", {
          key: "s", style: { fontSize: "clamp(7px,1.9cqh,20px)", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.78 },
        }, labels.subtitle) : null,
        labels.showCoords && labels.coords ? e("div", {
          key: "c", style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: "clamp(6px,1.5cqh,13px)", letterSpacing: "0.18em", opacity: 0.62, marginTop: "0.2em" },
        }, labels.coords) : null,
      ]) : null,
    ]);
  }

  // ---------- canvas renderer (poster -> PNG for cart preview / order) ----------
  // scale (optional) multiplies the marker artwork so the printed pin matches
  // the size the customer chose on screen.
  function drawMarker(ctx, style, theme, x, y, scale) {
    ctx.save(); ctx.translate(x, y);
    if (scale && scale !== 1) ctx.scale(scale, scale);
    const mk = theme.marker;
    if (style === "pin") {
      ctx.fillStyle = mk;
      ctx.beginPath(); ctx.arc(0, -50, 20, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.moveTo(-15, -42); ctx.lineTo(15, -42); ctx.lineTo(0, 0); ctx.closePath(); ctx.fill();
      ctx.fillStyle = theme.bg; ctx.beginPath(); ctx.arc(0, -50, 7.5, 0, Math.PI * 2); ctx.fill();
    } else if (style === "dot") {
      ctx.strokeStyle = mk; ctx.lineWidth = 6; ctx.beginPath(); ctx.arc(0, 0, 17, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = mk; ctx.beginPath(); ctx.arc(0, 0, 6, 0, Math.PI * 2); ctx.fill();
    } else if (style === "heart") {
      ctx.fillStyle = mk; ctx.beginPath();
      ctx.moveTo(0, -6);
      ctx.bezierCurveTo(-7, -22, -32, -18, -32, 2);
      ctx.bezierCurveTo(-32, 22, -10, 30, 0, 44);
      ctx.bezierCurveTo(10, 30, 32, 22, 32, 2);
      ctx.bezierCurveTo(32, -18, 7, -22, 0, -6);
      ctx.closePath(); ctx.fill();
    } else if (style === "house") {
      ctx.fillStyle = mk;
      // roof
      ctx.beginPath(); ctx.moveTo(0, -34); ctx.lineTo(30, -8); ctx.lineTo(-30, -8); ctx.closePath(); ctx.fill();
      // body
      ctx.beginPath(); ctx.rect(-22, -8, 44, 34); ctx.fill();
      // door (knockout to map background)
      ctx.fillStyle = theme.bg; ctx.beginPath(); ctx.rect(-7, 6, 14, 20); ctx.fill();
    }
    ctx.restore();
  }

  function spaced(ctx, txt, cx, cy, sp) {
    try { ctx.letterSpacing = sp + "px"; } catch (e) {}
    ctx.fillText(txt, cx + sp / 2, cy);
    try { ctx.letterSpacing = "0px"; } catch (e) {}
  }

  function capMetrics(CW, PH, k) {
    if (PH && k) return { titlePx: Math.round(PH * 0.054), subPx: Math.round(PH * 0.019), coordPx: Math.round(PH * 0.015), gap: 8 * k, ruleH: 5 * k, live: true };
    return { titlePx: Math.round(CW * 0.058), subPx: Math.round(CW * 0.024), coordPx: Math.round(CW * 0.019), gap: CW * 0.02, ruleH: null, live: false };
  }

  // Rows as [kind, boxHeight, fontPx]. In `live` mode the box heights mirror the
  // preview's flex layout exactly: its subtitle and coordinate rows are styled
  // line-height:0, so they occupy NO height and are separated only by the row gap.
  function capRows(labels, m) {
    const rows = [];
    if (labels.subtitleTop) rows.push(["rule", m.ruleH != null ? m.ruleH : m.subPx * 0.5, m.subPx]);
    rows.push(["title", m.live ? m.titlePx * 1.04 : m.titlePx, m.titlePx]);
    if (labels.subtitle) rows.push(["sub", m.live ? 0 : m.subPx, m.subPx]);
    if (labels.showCoords && labels.coords) rows.push(["coord", m.live ? 0 : m.coordPx, m.coordPx]);
    return rows;
  }

  function measureCaption(labels, CW, PH, k) {
    const m = capMetrics(CW, PH, k);
    const rows = capRows(labels, m);
    let total = -m.gap; rows.forEach((r) => (total += r[1] + m.gap));
    return Math.max(0, total);
  }

  function drawCaption(ctx, labels, theme, titleFont, CW, bandTop, bandH, shaped, isoTall, PH, k, rowCentres, ruleGeom) {
    const capC = theme.labelColor || theme.text;
    const cx = CW / 2;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    const m0 = capMetrics(CW, PH, k);
    let titlePx = m0.titlePx;
    let subPx = m0.subPx;
    let coordPx = m0.coordPx;
    let gap = m0.gap;
    const ruleH = m0.ruleH;
    const build = () => {
      const rows = capRows(labels, { ruleH, subPx, titlePx, coordPx, live: m0.live });
      let total = -gap; rows.forEach((r) => (total += r[1] + gap));
      return { rows, total };
    };
    let { rows, total } = build();
    // Scale the whole caption down if it would overflow the band height (the
    // landscape band is short — without this the coordinates line gets cut off).
    const avail = shaped ? bandH * 0.72 : bandH * 0.84;
    if (total > avail) {
      const k = avail / total;
      titlePx = Math.round(titlePx * k); subPx = Math.round(subPx * k);
      coordPx = Math.round(coordPx * k); gap = gap * k;
      ({ rows, total } = build());
    }
    let cy = isoTall ? bandTop + bandH * 0.12 : shaped ? bandTop + bandH * 0.2 : bandTop + bandH / 2 - total / 2;
    // When the caller measured the live preview's row positions, place each row on
    // its own measured centre — exact parity, no layout simulation.
    const exact = rowCentres && rowCentres.length === rows.length ? rowCentres : null;
    rows.forEach((r, ri) => {
      if (exact) cy = exact[ri]; else cy += r[1] / 2;
      if (r[0] === "rule") {
        const accent = theme.satellite ? capC : (theme.marker || theme.major); const ra = theme.satellite ? 0.62 : 1;
        if (ruleGeom) {
          // Measured from the preview: full span, dot diameter, line length.
          const half = ruleGeom.w / 2;
          ctx.strokeStyle = accent; ctx.globalAlpha = 0.6 * ra; ctx.lineWidth = ruleGeom.lineH;
          ctx.beginPath();
          ctx.moveTo(cx - half, cy); ctx.lineTo(cx - half + ruleGeom.line, cy);
          ctx.moveTo(cx + half - ruleGeom.line, cy); ctx.lineTo(cx + half, cy);
          ctx.stroke();
          ctx.fillStyle = accent; ctx.globalAlpha = ra;
          ctx.beginPath(); ctx.arc(cx, cy, ruleGeom.dot / 2, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;
        } else {
        const w = CW * 0.16;
        ctx.strokeStyle = accent; ctx.globalAlpha = 0.6 * ra; ctx.lineWidth = Math.max(1.5, CW * 0.0017);
        ctx.beginPath(); ctx.moveTo(cx - w, cy); ctx.lineTo(cx - CW * 0.012, cy);
        ctx.moveTo(cx + CW * 0.012, cy); ctx.lineTo(cx + w, cy); ctx.stroke();
        ctx.fillStyle = accent; ctx.globalAlpha = ra; ctx.beginPath(); ctx.arc(cx, cy, Math.max(3, CW * 0.004), 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
        }
      } else if (r[0] === "title") {
        ctx.fillStyle = capC; ctx.font = `600 ${r[2]}px ${titleFont}`;
        const txt = labels.upper ? (labels.title || "").toUpperCase() : (labels.title || "");
        spaced(ctx, txt, cx, cy, labels.spaced ? r[2] * 0.12 : r[2] * 0.02);
      } else if (r[0] === "sub") {
        ctx.fillStyle = theme.satellite ? capC : (theme.marker || capC); ctx.globalAlpha = theme.satellite ? 1 : 0.92;
        ctx.font = `600 ${r[2]}px 'Work Sans', sans-serif`;
        spaced(ctx, (labels.subtitle || "").toUpperCase(), cx, cy, r[2] * 0.3);
        ctx.globalAlpha = 1;
      } else if (r[0] === "coord") {
        ctx.fillStyle = capC; ctx.globalAlpha = 0.62;
        ctx.font = `400 ${r[2]}px 'IBM Plex Mono', monospace`;
        spaced(ctx, labels.coords, cx, cy, r[2] * 0.18);
        ctx.globalAlpha = 1;
      }
      if (!exact) cy += r[1] / 2 + gap;
    });
  }

  function renderPosterCanvas(opts) {
    const { city, theme, view, paper, labels, markerStyle, markerScale = 1, titleFont, detail = "rich", px = 2, baseW = 760 } = opts;
    const CW = baseW;
    const aspect = paper.aspect;
    const totalH = Math.round(CW * aspect);
    const band = labels.show ? (paper.landscape ? 0.2 : 0.18) : 0;
    const mapH = Math.round(totalH * (1 - band));
    const cv = document.createElement("canvas");
    cv.width = Math.round(CW * px); cv.height = Math.round(totalH * px);
    const ctx = cv.getContext("2d");
    ctx.scale(px, px);
    const Sz = MAP_SIZE;

    ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, CW, totalH);

    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, CW, mapH); ctx.clip();
    ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, CW, mapH);
    const sc = CW / view.vw;
    ctx.translate(CW / 2, mapH / 2);
    ctx.scale(sc, sc);
    ctx.translate(-view.cx, -view.cy);

    const stroke = (pts, color, w) => {
      if (w <= 0) return;
      ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
      ctx.strokeStyle = color; ctx.lineWidth = w; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.stroke();
    };
    const fill = (pts, color) => {
      ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.closePath();
      ctx.fillStyle = color; ctx.fill();
    };

    ctx.save();
    ctx.translate(Sz / 2, Sz / 2); ctx.rotate(city.rot); ctx.translate(-Sz / 2, -Sz / 2);
    city.lakes.forEach((p) => fill(p, theme.water));
    city.rivers.forEach((p) => stroke(p, theme.water, 30));
    city.parks.forEach((p) => fill(p, theme.park));
    const wMinor = detail === "minimal" ? 0 : 3.4;
    if (wMinor > 0) city.roads.minor.forEach((p) => stroke(p, theme.minor, wMinor));
    city.roads.major.forEach((p) => stroke(p, theme.major, 7));
    city.roads.highway.forEach((p) => stroke(p, theme.highway, 12));
    ctx.restore();

    drawMarker(ctx, markerStyle, theme, Sz / 2, Sz / 2, markerScale);
    ctx.restore();

    if (labels.show) drawCaption(ctx, labels, theme, titleFont, CW, mapH, totalH - mapH);
    return cv;
  }

  function renderPosterPNG(opts) {
    return renderPosterCanvas(opts).toDataURL("image/png");
  }

  window.MAP = { MAP_SIZE };
  window.generateCity = generateCity;
  window.THEMES = THEMES;
  window.MapPoster = MapPoster;
  window.renderPosterCanvas = renderPosterCanvas;
  window.renderPosterPNG = renderPosterPNG;
  window.drawMarker = drawMarker;
  window.drawCaption = drawCaption;
  window.measureCaption = measureCaption;
})();
